import PaymentForm from '../../components/payments/PaymentForm.jsx';
import { formatCurrency } from '../../lib/CurrencyHelpers.js';
import React, { useEffect, useState } from 'react';
import usePublicPostApi from '../lib/usePublicPostApi.js';
import Loading from '../../components/Loading';
import { ucfirst } from '../../lib/Utils.js';
import { getPaymentMethodDetails } from '~/lib/PaymentMethodHelpers';
import { useHandlePaymentReturnResponse } from '~/public/lib/Payments';
import ErrorMessage from '~/components/ErrorMessage';
import CouponInput, { getCodesFromUrl } from '~/public/components/CouponInput';
import { conversionTrackStep } from '~/public/lib/ConversionEventTracking.js';
import ErrorMessageBlock from '~/components/ErrorMessageBlock.jsx';
import ThemedForm from '~/components/forms/ThemedForm.jsx';
import FormBackgroundContainer from '~/components/forms/FormBackgroundContainer.jsx';
import PageContainer from '~/public/components/PageContainer.jsx';
import { saveLastPaymentParams } from '~/public/components/FormPaymentFormWithSavedParams.jsx';
import * as Sentry from '@sentry/react';

const OrderSummary = ({ lineItems }) => {
    return (
        <div className="">
            <div className="px-6 py-4">
                {lineItems.items?.map((item, idx) => (
                    <div key={idx} className="py-2">
                        <div className="flex items-center justify-between">
                            <span className="">{item.name}</span>
                            <span className="ml-1 text-nowrap">
                                {formatCurrency(item.price)}
                            </span>
                        </div>
                        {!!item.details && (
                            <div className="text-sm text-gray-500">
                                {item.details}
                            </div>
                        )}
                        {!!item.error && (
                            <div className="text-sm text-red-600">
                                {item.error}
                            </div>
                        )}
                    </div>
                ))}
            </div>
            <div className="font-semibold border-t py-4 pl-6">
                {lineItems.total_owed < lineItems.total && (
                    <div>
                        <div>Total: {formatCurrency(lineItems.total_owed)}</div>
                        <div className="font-normal mt-1 text-sm">
                            {lineItems.total_owed > 0 && (
                                <>
                                    Total is {formatCurrency(lineItems.total)},{' '}
                                    but only{' '}
                                    {formatCurrency(lineItems.total_owed)} is
                                    owed now
                                </>
                            )}
                            {lineItems.total_owed <= 0 && (
                                <>
                                    You will be charged nothing today, your
                                    payment information will be saved for later.
                                </>
                            )}
                        </div>
                    </div>
                )}
                {lineItems.total_owed >= lineItems.total && (
                    <>Total: {formatCurrency(lineItems.total_owed)}</>
                )}
            </div>
        </div>
    );
};

const ChoosePaymentOption = ({
    paymentOptions,
    selectedOption,
    onChangePaymentParams,
    paymentParams,
    totalOwed,
}) => {
    const [localSelectedOption, setLocalSelectedOption] =
        useState(selectedOption);
    useEffect(() => {
        setLocalSelectedOption(selectedOption);
    }, [selectedOption]);

    const optToHuman = {
        vault: 'Save Card on File',
        full: 'Pay in Full Now',
    };
    const optToDescription = {
        vault: totalOwed <= 0 ? 'No payment now' : 'Pay less now',
        full: 'You will be charged now',
        skip: 'Skip payment',
    };

    return (
        <div className=" mb-2 mt-4">
            <div className="input-label">Payment Option</div>
            <div className="flex items-center space-x-4">
                {paymentOptions.map(opt => (
                    <div
                        className={`border-0 ${localSelectedOption === opt ? 'border-2 themed-border' : 'border-gray-100 border-2'} flex-1 panel cursor-pointer p-6 flex`}
                        onClick={() => {
                            onChangePaymentParams({
                                ...paymentParams,
                                paymentType: opt,
                            });
                            setLocalSelectedOption(opt);
                        }}
                    >
                        <input
                            type="radio"
                            checked={localSelectedOption === opt}
                            className="mr-3 mt-0.5"
                        />
                        <div>
                            <div className="font-semibold text-lg">
                                {optToHuman[opt] || ucfirst(opt)}
                            </div>
                            <div className="text-sm text-gray-500 mt-1">
                                {optToDescription[opt] || ''}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const SavedPaymentMethod = ({
    isSelected,
    onSelect,
    method,
    isFirst,
    onSubmit,
    isPaying,
}) => {
    const { title, description } = getPaymentMethodDetails(method);

    return (
        <label className={`flex items-start`}>
            <div className="pt-3">
                <input
                    type="radio"
                    checked={isSelected}
                    onChange={e => e.target.checked && onSelect()}
                />
            </div>
            <div
                className={`py-3 ml-4 pr-4 flex-1 ${!isFirst ? 'border-t' : ''}`}
            >
                <div className="cursor-pointer">{title}</div>
                <div className="text-sm text-gray-500 cursor-pointer">
                    {description}
                </div>
                {isSelected && (
                    <button
                        type="button"
                        className="form-button mt-2"
                        onClick={onSubmit}
                    >
                        {isPaying ? 'Submitting...' : 'Use this card'}
                    </button>
                )}
            </div>
        </label>
    );
};

const FormPaymentForm = ({
    paymentSettings: initialPaymentSettings,
    submission,
    lineItems: initialLineItems,
}) => {
    const [error, setError] = useState(null);
    const [selectedSavedPayment, setSelectedSavedPayment] = useState(null);
    const [paymentParams, setPaymentParams] = useState({});
    const [paymentSettings, setPaymentSettings] = useState(
        initialPaymentSettings,
    );
    const [lineItems, setLineItems] = useState(initialLineItems);
    const { save: savePaymentParams, isSaving: isSavingPaymentParams } =
        usePublicPostApi(`/payment/submission/${submission.token}`);

    const { save: savePayWithSavedCard, isSaving: isPayingWithSavedCard } =
        usePublicPostApi(`/payments/saved-method/charge`);

    const { save: saveSkipPayment, isSaving: isSkippingPayment } =
        usePublicPostApi(`/payments/skip-payment`);

    function onChangePaymentParams(newParams) {
        setPaymentParams(newParams);
        savePaymentParams({
            params: newParams,
        }).then(result => {
            setPaymentSettings(result?.payment);
            setLineItems(result.lineItems);
        });
    }

    useEffect(() => {
        saveLastPaymentParams(paymentSettings, submission, lineItems);
    }, [paymentSettings, submission, lineItems]);

    useEffect(() => {
        const paramCodes = getCodesFromUrl();
        if (paramCodes.length > 0) {
            onChangePaymentParams({
                ...paymentParams,
                codes: paramCodes,
            });
        }

        // Track that we viewed this payment step
        conversionTrackStep('InitiateCheckout', {});
    }, []);

    const { handleResponse } = useHandlePaymentReturnResponse();
    function payWithSavedCard(method) {
        setError(null);
        savePayWithSavedCard({
            savedMethod: method.id,
            submissionToken: submission.token,
            params: paymentParams,
        })
            .then(result => {
                handleResponse(result);
            })
            .catch(err => {
                const message = err.response?.data?.message;
                setError(
                    `Failed to process payment, please try again. ${message}`,
                );

                // Report error to Sentry
                Sentry.withScope(scope => {
                    scope.setContext('payment_error', {
                        error_type: 'saved_card_payment_failed',
                        saved_method_id: method.id,
                        submission_token: submission.token,
                        error_message: message,
                        status_code: err.response?.status,
                        response_data: err.response?.data,
                    });
                    scope.setLevel('error');

                    Sentry.captureException(err);
                });
            });
    }

    function skipPayment() {
        setError(null);
        saveSkipPayment({
            submissionToken: submission.token,
            params: paymentParams,
        })
            .then(result => {
                handleResponse(result);
            })
            .catch(err => {
                const message = err.response?.data?.message;
                setError(`Unable to skip payment. ${message}`);
            });
    }

    let paymentBtnLabel = `Pay ${formatCurrency(lineItems.total_owed)} Now`;
    if (lineItems.total_owed <= 0 && lineItems.requires_vaulting) {
        paymentBtnLabel = 'Save Payment Information';
    }

    return (
        <div className="md:flex form-section-width">
            <div className="lg:flex-1">
                {lineItems?.payment_options?.length > 1 && (
                    <ChoosePaymentOption
                        paymentOptions={lineItems?.payment_options}
                        selectedOption={lineItems.payment_option_selected}
                        totalOwed={lineItems.total_owed}
                        paymentParams={paymentParams}
                        onChangePaymentParams={onChangePaymentParams}
                    />
                )}
                {isSavingPaymentParams && <Loading />}
                {!isSavingPaymentParams &&
                    lineItems.payment_option_selected !== 'skip' && (
                        <div className="mt-4">
                            {lineItems?.payment_options?.length > 1 && (
                                <div className="input-label">
                                    Payment Details
                                </div>
                            )}

                            {!!error && (
                                <ErrorMessage className="py-4">
                                    {error}
                                </ErrorMessage>
                            )}

                            {paymentSettings.savedPaymentMethods?.length >
                                0 && (
                                <div>
                                    <div className="panel pl-4">
                                        {paymentSettings.savedPaymentMethods.map(
                                            (method, idx) => (
                                                <SavedPaymentMethod
                                                    isSelected={
                                                        selectedSavedPayment ===
                                                        method.id
                                                    }
                                                    onSelect={() =>
                                                        setSelectedSavedPayment(
                                                            method.id,
                                                        )
                                                    }
                                                    isFirst={idx === 0}
                                                    method={method}
                                                    onSubmit={() => {
                                                        payWithSavedCard(
                                                            method,
                                                        );
                                                    }}
                                                    isPaying={
                                                        isPayingWithSavedCard
                                                    }
                                                />
                                            ),
                                        )}
                                    </div>

                                    <div className="panel p-4 mt-4">
                                        <div>
                                            <label className="flex items-center">
                                                <input
                                                    type="radio"
                                                    checked={
                                                        !selectedSavedPayment
                                                    }
                                                    onChange={e =>
                                                        e.target.checked &&
                                                        setSelectedSavedPayment(
                                                            null,
                                                        )
                                                    }
                                                />
                                                <div className="ml-4">
                                                    Other payment options
                                                </div>
                                            </label>
                                            {!selectedSavedPayment && (
                                                <PaymentForm
                                                    type="formsubmission"
                                                    id={submission.id}
                                                    paymentSettings={
                                                        paymentSettings
                                                    }
                                                    paymentBtnLabel={
                                                        paymentBtnLabel
                                                    }
                                                    paymentElementContainerClassName="pb-6 form-section-container mb-0 mt-4"
                                                />
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                            {!paymentSettings.savedPaymentMethods?.length && (
                                <PaymentForm
                                    type="formsubmission"
                                    id={submission.id}
                                    paymentSettings={paymentSettings}
                                    paymentBtnLabel={paymentBtnLabel}
                                    paymentElementContainerClassName="pb-6 form-section-container mb-0 mt-0"
                                />
                            )}
                        </div>
                    )}
                {lineItems.payment_option_selected === 'skip' && (
                    <div className="mt-4">
                        <div className="mb-2">
                            You're all set! No payment is required at this time.
                        </div>
                        <button
                            type="button"
                            className="form-button skip-payment-button"
                            onClick={skipPayment}
                        >
                            Complete
                        </button>
                    </div>
                )}
            </div>
            <div className="flex flex-col md:ml-8">
                <div className="form-section-container p-0 min-w-64 sm:w-64">
                    <div className="p-4 text-center rounded-lg bg-gray-200 font-semibold">
                        Summary
                    </div>
                    <div>
                        <OrderSummary lineItems={lineItems} />
                        <CouponInput
                            paymentParams={paymentParams}
                            onChangePaymentParams={onChangePaymentParams}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FormPaymentForm;
